import React from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, 
  Monitor, 
  Cloud, 
  Headphones, 
  CheckCircle, 
  ArrowRight,
  Star,
  TrendingUp,
  Lock,
  Users,
  Clock,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import heroImage from '@/assets/hero-it-services.jpg';

const Index = () => {
  const services = [
    {
      icon: Monitor,
      title: 'Managed IT Services',
      description: 'Comprehensive IT management including network monitoring, system administration, and proactive maintenance.',
      features: ['24/7 Monitoring', 'Proactive Maintenance', 'System Administration']
    },
    {
      icon: Shield,
      title: 'Cybersecurity Solutions',
      description: 'Advanced threat protection, firewall management, and security monitoring to keep your business safe.',
      features: ['Threat Detection', 'Firewall Management', 'Security Audits']
    },
    {
      icon: Cloud,
      title: 'Cloud Services',
      description: 'Secure cloud migration, management, and optimization for enhanced scalability and cost-effectiveness.',
      features: ['Cloud Migration', 'Data Backup', 'Cost Optimization']
    },
    {
      icon: Headphones,
      title: '24/7 Support',
      description: 'Round-the-clock technical support and remote assistance to keep your business running smoothly.',
      features: ['Remote Support', 'Help Desk', 'Emergency Response']
    }
  ];

  const stats = [
    { value: '500+', label: 'Clients Protected' },
    { value: '99.9%', label: 'Uptime Guarantee' },
    { value: '24/7', label: 'Support Available' },
    { value: '15+', label: 'Years Experience' }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      title: 'IT Director, TechForward Manufacturing',
      text: 'Zentech Solutions transformed our IT infrastructure. Their proactive approach has eliminated downtime and significantly improved our security posture.',
      rating: 5
    },
    {
      name: 'Dr. Michael Chen',
      title: 'CIO, HealthCare Plus',
      text: 'Their cybersecurity expertise and GDPR compliance solutions have been invaluable for our healthcare organization. Highly recommended.',
      rating: 5
    }
  ];

  return (
    <Layout>
      {/* Hero Slider Section */}
      <section className="relative">
        <Carousel 
          className="w-full" 
          opts={{ align: "start", loop: true }}
          plugins={[
            Autoplay({
              delay: 2000,
            }),
          ]}
        >
          <CarouselContent>
            <CarouselItem>
              <div className="relative py-20 lg:py-32">
                <div 
                  className="absolute inset-0 bg-cover bg-center bg-no-repeat [filter:none]"
                  style={{ backgroundImage: `url(${heroImage})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary-dark/80"></div>
                </div>
                <div className="relative container mx-auto px-4">
                  <div className="max-w-4xl mx-auto text-center text-primary-foreground">
                    <Badge className="mb-6 bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30">
                      Trusted IT Solutions Since 2009
                    </Badge>
                    <h1 className="text-4xl lg:text-7xl font-bold mb-6 leading-tight">
                      Secure Managed IT Services for Modern Businesses
                    </h1>
                    <p className="text-xl lg:text-2xl mb-8 text-primary-foreground/90 leading-relaxed">
                      Protect, optimize, and scale your business with our comprehensive managed IT services 
                      and enterprise-grade cybersecurity solutions.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button asChild size="lg" variant="secondary" className="text-lg">
                        <Link to="/contact">Get Free Consultation</Link>
                      </Button>
                      <Button asChild variant="outline" size="lg" className="text-lg text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary">
                        <Link to="/cybersecurity">Explore Cybersecurity</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>
            <CarouselItem>
              <div className="relative py-20 lg:py-32">
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary-light"></div>
                <div className="relative container mx-auto px-4">
                  <div className="max-w-4xl mx-auto text-center text-primary-foreground">
                    <Badge className="mb-6 bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30">
                      Advanced Cybersecurity
                    </Badge>
                    <h1 className="text-4xl lg:text-7xl font-bold mb-6 leading-tight">
                      Protect Your Business from Cyber Threats
                    </h1>
                    <p className="text-xl lg:text-2xl mb-8 text-primary-foreground/90 leading-relaxed">
                      Advanced threat detection, firewall management, and 24/7 security monitoring 
                      to keep your business safe from evolving cyber threats.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button asChild size="lg" variant="secondary" className="text-lg">
                        <Link to="/cybersecurity">Learn About Security</Link>
                      </Button>
                      <Button asChild variant="outline" size="lg" className="text-lg text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary">
                        <Link to="/contact">Get Security Assessment</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>
            <CarouselItem>
              <div className="relative py-20 lg:py-32">
                <div className="absolute inset-0 bg-gradient-to-br from-primary-dark to-primary"></div>
                <div className="relative container mx-auto px-4">
                  <div className="max-w-4xl mx-auto text-center text-primary-foreground">
                    <Badge className="mb-6 bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30">
                      24/7 Support & Monitoring
                    </Badge>
                    <h1 className="text-4xl lg:text-7xl font-bold mb-6 leading-tight">
                      Always-On IT Support for Your Business
                    </h1>
                    <p className="text-xl lg:text-2xl mb-8 text-primary-foreground/90 leading-relaxed">
                      Round-the-clock monitoring, proactive maintenance, and instant support 
                      to ensure your technology never becomes a roadblock.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button asChild size="lg" variant="secondary" className="text-lg">
                        <Link to="/services">View All Services</Link>
                      </Button>
                      <Button asChild variant="outline" size="lg" className="text-lg text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary">
                        <Link to="/contact">Start Free Trial</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>
          </CarouselContent>
          <CarouselPrevious className="left-4 bg-primary-foreground/20 border-primary-foreground/30 hover:bg-primary-foreground/30" />
          <CarouselNext className="right-4 bg-primary-foreground/20 border-primary-foreground/30 hover:bg-primary-foreground/30" />
        </Carousel>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <Card key={index} className="text-center border-none shadow-lg">
                <CardContent className="p-8">
                  <div className="text-4xl lg:text-5xl font-bold text-primary mb-2">{stat.value}</div>
                  <div className="text-muted-foreground">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-4">
              Comprehensive IT Solutions
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              From proactive monitoring to advanced cybersecurity, we provide complete IT management 
              solutions that keep your business secure, efficient, and ahead of the competition.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="text-center border-none shadow-lg hover:shadow-xl transition-shadow h-full">
                <CardContent className="p-8">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <service.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">{service.title}</h3>
                  <p className="text-muted-foreground mb-6">{service.description}</p>
                  <div className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center justify-center space-x-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-success" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg">
              <Link to="/services">
                View All Services <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
                Why Businesses Trust Zentech Solutions
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                With over 15 years of experience in managed IT services and cybersecurity, 
                we understand the unique challenges facing modern businesses. Our proactive 
                approach ensures your technology supports your growth rather than holding you back.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { icon: TrendingUp, title: 'Business Growth', desc: 'IT solutions that scale with your business' },
                  { icon: Lock, title: 'Enterprise Security', desc: 'Advanced cybersecurity protection' },
                  { icon: Users, title: 'Expert Team', desc: 'Certified IT professionals' },
                  { icon: Clock, title: 'Proactive Support', desc: '24/7 monitoring and maintenance' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="bg-primary/10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Card className="bg-gradient-to-br from-primary to-primary-light text-primary-foreground border-none shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6">Ready to Get Started?</h3>
                  <p className="text-primary-foreground/90 mb-6">
                    Schedule a free consultation with our IT experts to assess your current 
                    infrastructure and discuss how we can help optimize your technology.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 text-sm">
                      <CheckCircle className="h-4 w-4" />
                      <span>Free initial assessment</span>
                    </div>
                    <div className="flex items-center space-x-3 text-sm">
                      <CheckCircle className="h-4 w-4" />
                      <span>Custom solution design</span>
                    </div>
                    <div className="flex items-center space-x-3 text-sm">
                      <CheckCircle className="h-4 w-4" />
                      <span>Transparent pricing</span>
                    </div>
                  </div>
                  <Button asChild variant="secondary" size="lg" className="w-full mt-6">
                    <Link to="/contact">Schedule Free Assessment</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Don't just take our word for it. See what our satisfied clients have to say 
              about our managed IT services and cybersecurity solutions.
            </p>
          </div>

          <Carousel 
            className="w-full max-w-4xl mx-auto" 
            opts={{ align: "start", loop: true }}
            plugins={[
              Autoplay({
                delay: 2000,
              }),
            ]}
          >
            <CarouselContent>
              {testimonials.map((testimonial, index) => (
                <CarouselItem key={index} className="md:basis-1/2">
                  <Card className="border-none shadow-lg h-full">
                    <CardContent className="p-8">
                      <div className="flex items-center space-x-1 mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 fill-current text-yellow-400" />
                        ))}
                      </div>
                      <p className="text-muted-foreground italic mb-6">"{testimonial.text}"</p>
                      <div>
                        <div className="font-semibold text-foreground">{testimonial.name}</div>
                        <div className="text-sm text-muted-foreground">{testimonial.title}</div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>

          <div className="text-center mt-12">
            <Button asChild variant="outline" size="lg">
              <Link to="/testimonials">
                Read More Testimonials <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <Card className="bg-gradient-to-r from-primary to-primary-light text-primary-foreground border-none">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl lg:text-5xl font-bold mb-6">
                Secure Your Business Today
              </h2>
              <p className="text-xl text-primary-foreground/90 mb-8 max-w-3xl mx-auto">
                Don't wait for a cyber attack or system failure to invest in proper IT management. 
                Contact our experts today for a free consultation and protect your business now.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild variant="secondary" size="lg" className="text-lg">
                  <Link to="/contact">Get Free Consultation</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-lg text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary">
                  <Link to="/cybersecurity">Contact Security Experts</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
